import React, { useState, useRef, useEffect } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  HStack,
  Avatar,
  Box,
  Text,
  Icon,
  Flex,
  Badge,
  VStack
} from '@chakra-ui/react';
import { FiChevronDown, FiSettings, FiLogOut, FiMail } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';

const UserMenu = ({ user, logout }) => {
  const navigate = useNavigate();
  
  // Extract username from email (part before @)
  const getUsernameFromEmail = () => {
    if (!user) return 'Guest';
    
    // Try to get username from email first
    if (user.email) {
      return user.email.split('@')[0];
    }
    
    // Fallback to name field
    if (user.name) {
      return user.name;
    }
    
    // Fallback to fullname field
    if (user.fullname) {
      return user.fullname;
    }
    
    return 'User';
  };
  
  // Generate avatar letter from username
  const getInitial = () => {
    const username = getUsernameFromEmail();
    return username.charAt(0).toUpperCase();
  };
  
  // Get avatar color based on username - for consistency
  const getAvatarColor = () => {
    if (!user) return 'gray.500';
    
    const colors = ['green.400', 'green.500', 'green.600', 'green.700'];
    const username = getUsernameFromEmail();
    const charCode = username.charCodeAt(0) || 0;
    return colors[charCode % colors.length];
  };
  
  // Get role badge props
  const getRoleBadgeProps = () => {
    if (!user) return { bg: 'gray.100', color: 'gray.600', label: 'Guest' };
    
    switch (user.role) {
      case 1: 
        return { bg: 'rgba(52, 211, 153, 0.15)', color: '#34d399', label: 'User', className: 'user' };
      case 2: 
        return { bg: 'rgba(96, 165, 250, 0.15)', color: '#60a5fa', label: 'Team', className: 'team' };
      case 3: 
        return { bg: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', label: 'Admin', className: 'admin' };
      default:
        return { bg: 'rgba(52, 211, 153, 0.15)', color: '#34d399', label: 'User', className: 'user' };
    }
  };
  
  const roleBadge = getRoleBadgeProps();
  const username = getUsernameFromEmail();
  
  // Enhanced logout handler
  const handleLogout = async () => {
    try {
      await logout();
      // Navigation will be handled by the logout function in AuthContext
    } catch (error) {
 
      // Force navigation to login even if logout fails
      navigate('/login', { replace: true });
    }
  };

  return (
    <Box className="user-dropdown">
      <Menu>
        <MenuButton
          as={Button}
          variant="ghost"
          size="sm"
          display="flex"
          alignItems="center"
          p={2}
          borderRadius="lg"
          _hover={{ 
            bg: "whiteAlpha.200",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
          }}
          _active={{ transform: "translateY(0)" }}
          transition="all 0.2s ease"
          bg="whiteAlpha.100"
          backdropFilter="blur(10px)"
          border="1px solid"
          borderColor="whiteAlpha.200"
        >
          <HStack spacing={2}>
            <Avatar 
              size="sm" 
              name={getInitial()} 
              bg="{getAvatarColor()}"
              color="white"
              fontWeight="bold"
              className="user-avatar"
              border="2px solid"
              borderColor="whiteAlpha.300"
              _hover={{ borderColor: "green.400" }}
              transition="all 0.2s ease"
            />
            <Box display={{ base: 'none', md: 'block' }}>
              <Icon 
                as={FiChevronDown} 
                fontSize="sm" 
                color="gray.300" 
                _groupHover={{ color: "white" }}
                transition="all 0.2s ease"
              />
            </Box>
          </HStack>
        </MenuButton>

        <MenuList 
          bg="rgba(15, 27, 14, 0.8)" 
          borderColor="rgba(78, 224, 53, 0.3)" 
          borderWidth="1px"
          boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" 
          className="user-dropdown-menu"
          zIndex={1000}
          backdropFilter="blur(20px)"
          borderRadius="xl"
          overflow="hidden"
          minW="280px"
        >
          {/* Enhanced User Info Section */}
          <Box 
            p={5} 
            borderBottom="1px solid" 
            borderColor="rgba(78, 224, 53, 0.2)"
            bg="rgba(15, 27, 14, 0.8)"
          >
            <Flex align="center" gap={4}>
              <Avatar
                size="lg"
                name={getInitial()}
                bg={getAvatarColor()}
                color="white"
                fontWeight="bold"
                border="3px solid"
                borderColor="green.400"
                boxShadow="0 0 0 2px rgba(34, 197, 94, 0.2)"
              />
              <Box flex="1">
                <VStack align="start" spacing={1}>
                  <Text 
                    fontWeight="semibold" 
                    color="white"
                    fontSize="lg"
                    letterSpacing="tight"
                  >
                    {username}
                  </Text>
                  {user?.email && (
                    <HStack spacing={1}>
                      <Icon as={FiMail} boxSize={3} color="gray.400" />
                      <Text 
                        fontSize="xs" 
                        color="gray.400"
                        fontFamily="mono"
                      >
                        {user.email}
                      </Text>
                    </HStack>
                  )}
                  <Badge
                    fontSize="xs" 
                    px={2} 
                    py={1} 
                    borderRadius="md" 
                    bg={roleBadge.bg}
                    color={roleBadge.color}
                    className={`role-badge ${roleBadge.className}`}
                    fontWeight="medium"
                    textTransform="capitalize"
                  >
                    {roleBadge.label}
                  </Badge>
                </VStack>
              </Box>
            </Flex>
          </Box>
          
          {/* Menu Items */}
          <Box py={2}>
            <Link to="/api-settings">
              <MenuItem 
                icon={<Icon as={FiSettings} boxSize={4} color="blue.400" />}
                py={3}
                px={5}
                bg="rgba(15, 27, 14, 0.8)"
                _hover={{ 
                  bg: "rgba(15, 27, 14, 0.8)",
                  borderLeft: "3px solid",
                  borderColor: "blue.400"
                }}
                _focus={{ bg: "rgba(15, 27, 14, 0.8)" }}
                transition="all 0.2s ease"
                borderRadius="none"
              >
                <Text color="white" fontWeight="medium">Settings</Text>
              </MenuItem>
            </Link>
            
            <MenuDivider 
              borderColor="rgba(78, 224, 53, 0.2)" 
              my={2}
              mx={3}
            />
            
            <MenuItem 
              icon={<Icon as={FiLogOut} boxSize={4} color="red.400" />}
              onClick={handleLogout}
              py={3}
              bg="rgba(15, 27, 14, 0.8)"
              px={5}
              _hover={{ 
                bg: "rgba(15, 27, 14, 0.8)",
                borderLeft: "3px solid",
                borderColor: "red.400"
              }}
              _focus={{ bg: "rgba(15, 27, 14, 0.8)" }}
              color="red.400"
              transition="all 0.2s ease"
              borderRadius="none"
            >
              <Text fontWeight="medium">Logout</Text>
            </MenuItem>
          </Box>
        </MenuList>
      </Menu>
    </Box>
  );
};

export default UserMenu;